/**
 * How closely a mapping candidate matched the record's raw label.
 *
 * This is a SEPARATE vocabulary from `confidence_band.ts`, on purpose. A confidence
 * band describes how well the pipeline read a cell, and its bands carry a historical
 * accuracy reference measured against the CCH golden. A candidate's `score` measures
 * something else entirely — how close a catalogue name is to an extracted label — so
 * borrowing that chip would attach a calibration claim that does not hold here.
 */
export type MatchStrength = 'name' | 'strong' | 'possible' | 'weak';

/**
 * Lower bound (inclusive) of a deterministic name match. Mirrors the mapper's
 * `NORMALISED_SCORE`, the weakest of its three name tiers (exact 1.0, prefix-strip
 * 0.95, normalised 0.9) — at or above this the catalogue NAME matched, not a guess.
 */
export const NAME_MATCH_THRESHOLD = 0.9;

/**
 * Lower bound (inclusive) of a strong match. Mirrors the mapper's
 * `EMBEDDING_SIMILARITY_FLOOR` — the bar it requires before it will map a semantic
 * candidate itself, so a candidate at or above it is one the system would have taken.
 */
export const STRONG_MATCH_THRESHOLD = 0.8;

/**
 * Lower bound (inclusive) of a plausible match. Mirrors the mapper's
 * `SIMILARITY_FLOOR` — the floor for entering the shortlist at all. Below it a
 * candidate is only present because the mapper attaches the nearest three with no
 * floor rather than leave the reviewer an empty picker.
 */
export const POSSIBLE_MATCH_THRESHOLD = 0.3;

/**
 * Derive the match strength from a candidate's resolver score.
 * @param {number} score Resolver score in [0, 1].
 * @returns {MatchStrength}
 */
export function deriveMatchStrength(score: number): MatchStrength {
  if (score >= NAME_MATCH_THRESHOLD) {
    return 'name';
  }
  if (score >= STRONG_MATCH_THRESHOLD) {
    return 'strong';
  }
  if (score >= POSSIBLE_MATCH_THRESHOLD) {
    return 'possible';
  }
  return 'weak';
}

/** Short reviewer-facing label per strength — what the chip reads. */
export const MATCH_STRENGTH_LABEL: Record<MatchStrength, string> = {
  name: 'Name match',
  strong: 'Strong',
  possible: 'Possible',
  weak: 'Weak'
};

/** MUI palette colour per strength; mirrors the confidence palette's good/warn/bad. */
export const MATCH_STRENGTH_COLOR: Record<MatchStrength, 'success' | 'warning' | 'error'> = {
  name: 'success',
  strong: 'success',
  possible: 'warning',
  weak: 'error'
};

/** One sentence explaining what the strength means, for the chip's tooltip. */
export const MATCH_STRENGTH_HELP: Record<MatchStrength, string> = {
  name: 'The catalogue name matched the extracted label.',
  strong: 'Close enough that the system would map it on its own — confirm it still fits.',
  possible: 'A plausible match the system would not take unaided. Check it against the source.',
  weak: 'Below the matching floor. Offered only so the list is not empty — expect to search instead.'
};

/**
 * Accessible/tooltip description of one candidate's score: the strength, what it
 * means, and the raw number the strength was derived from. The number stays
 * available (a reviewer comparing two candidates may want it) but stops competing
 * with the name for attention.
 * @param {number} score Resolver score in [0, 1].
 * @returns {string}
 */
export function describeMatchStrength(score: number): string {
  const strength = deriveMatchStrength(score);
  return `${MATCH_STRENGTH_LABEL[strength]} — ${MATCH_STRENGTH_HELP[strength]} (match score ${score.toFixed(2)})`;
}
